import React, { useState } from 'react';

function HelpModal({ onClose }) {
  const [activeSection, setActiveSection] = useState('quickstart');

  const sections = {
    quickstart: 'Quick Start',
    liquidity: 'Liquidity',
    matching: 'Matching System',
    settings: 'Settings & Preferences',
    security: 'Security & Privacy'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#1a1a2e',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #333',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #333'
        }}>
          <h2 style={{ margin: 0, color: '#fff', fontSize: '20px' }}>
            Help & Documentation
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
              lineHeight: 1
            }}
          >
            &times;
          </button>
        </div>

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #333',
          backgroundColor: '#16162a'
        }}>
          {Object.entries(sections).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              style={{
                padding: '12px 20px',
                background: activeSection === key ? '#1a1a2e' : 'transparent',
                border: 'none',
                borderBottom: activeSection === key ? '2px solid #26a69a' : '2px solid transparent',
                color: activeSection === key ? '#26a69a' : '#888',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeSection === key ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1,
          color: '#ccc',
          lineHeight: '1.7'
        }}>
          {activeSection === 'quickstart' && <QuickStartContent />}
          {activeSection === 'matching' && <MatchingContent />}
          {activeSection === 'settings' && <SettingsContent />}
          {activeSection === 'security' && <SecurityContent />}
          {activeSection === 'liquidity' && <LiquidityContent />}
        </div>
      </div>
    </div>
  );
}

function QuickStartContent() {
  return (
    <div>
      <h3 style={{ color: '#26a69a', marginTop: 0 }}>Quick Start Guide</h3>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>1. Configure Your Settings</h4>
        <p>
          Click the <strong style={{ color: '#26a69a' }}>Settings</strong> button in the order form to configure:
        </p>
        <ul style={{ marginLeft: '20px' }}>
          <li><strong>Matching Window:</strong> How long to wait for counterparties (5-60 seconds)</li>
          <li><strong>Authorized Liquidity Participants:</strong> Which types of counterparties you want to trade with</li>
          <li><strong>My Liquidity Provider Type:</strong> Your classification for receiving inbound requests</li>
        </ul>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>2. Initiate a Matching Request</h4>
        <p>To find a counterparty for your trade:</p>
        <ol style={{ marginLeft: '20px' }}>
          <li>Select BUY or SELL</li>
          <li>Enter your quantity and limit price</li>
          <li>Click <strong style={{ color: '#26a69a' }}>Request for Matching</strong></li>
          <li>Wait for the matching window to complete</li>
          <li>Review the result when notified</li>
        </ol>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>3. Respond to Inbound Requests</h4>
        <p>When another participant initiates a matching request:</p>
        <ol style={{ marginLeft: '20px' }}>
          <li>A notification popup will appear with the ticker and time remaining</li>
          <li>Click the <strong style={{ color: '#26a69a' }}>Bid</strong> and/or <strong style={{ color: '#26a69a' }}>Offer</strong> box to fill in your quote — you can respond with a bid only, an offer only, a two-way price, or pass entirely</li>
          <li>Click <strong style={{ color: '#26a69a' }}>Submit Response</strong></li>
          <li>Wait for the matching result</li>
        </ol>
      </div>

    </div>
  );
}

function MatchingContent() {
  return (
    <div>
      <h3 style={{ color: '#26a69a', marginTop: 0 }}>How Matching Works</h3>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>Privacy-Preserving Matching</h4>
        <p>
          All order matching occurs inside a secure enclave using advanced encryption. Your order details
          (side, quantity, price) are encrypted before leaving your browser and can only be decrypted
          inside the secure enclave. No one - not even the platform operators - can see your order
          before matching occurs.
        </p>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>Matching Process</h4>
        <ol style={{ marginLeft: '20px' }}>
          <li><strong>Broadcast:</strong> When you initiate a request, eligible participants are notified</li>
          <li><strong>Collection:</strong> Responses are collected during the matching window</li>
          <li><strong>Decryption:</strong> The enclave decrypts all submissions simultaneously</li>
          <li><strong>Matching:</strong> Orders are matched based on price improvement and quantity</li>
          <li><strong>Result:</strong> Matched parties are notified with execution details</li>
        </ol>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>Matching Criteria</h4>
        <p>For a match to occur:</p>
        <ul style={{ marginLeft: '20px' }}>
          <li>Orders must be on opposite sides (one BUY, one SELL)</li>
          <li>Buy price must be &ge; Sell price (prices must cross)</li>
          <li>The responder's quantity must be <strong>at least as large</strong> as the initiator's quantity</li>
          <li>Both parties must have authorized each other's participant type</li>
        </ul>
        <p style={{ marginTop: '12px', color: '#999', fontSize: '14px' }}>
          Note: The executed quantity is always the initiator's full requested quantity, ensuring complete fills for the initiating party.
        </p>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>Execution Price</h4>
        <p>
          When orders match, the execution price is the <strong>responder's price</strong>.
          This benefits the initiator who receives price improvement from the liquidity provider.
          For example, if the initiator places a buy order at 100,000 and a responder offers to sell at 99,500,
          the trade executes at 99,500 - giving the initiator a better price than their limit.
        </p>
      </div>

      <div style={{
        backgroundColor: 'rgba(239, 83, 80, 0.1)',
        border: '1px solid rgba(239, 83, 80, 0.3)',
        borderRadius: '8px',
        padding: '16px'
      }}>
        <strong style={{ color: '#ef5350' }}>Important:</strong> The matching window cannot be extended
        once started. Make sure to respond promptly to inbound requests.
      </div>
    </div>
  );
}

function SettingsContent() {
  return (
    <div>
      <h3 style={{ color: '#26a69a', marginTop: 0 }}>Settings & Preferences</h3>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>Matching Window</h4>
        <p>
          The time period during which counterparties can respond to your matching request.
          Shorter windows mean faster results but potentially fewer responses. Longer windows
          allow more participants to respond.
        </p>
        <ul style={{ marginLeft: '20px' }}>
          <li><strong>5-10 seconds:</strong> Quick matching, best for liquid markets</li>
          <li><strong>15-30 seconds:</strong> Balanced (recommended)</li>
          <li><strong>60 seconds:</strong> Maximum participation, best for large orders</li>
        </ul>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>Authorized Liquidity Participants</h4>
        <p>
          Select which types of counterparties you're willing to trade with. Your request will
          only be broadcast to participants matching your selection.
        </p>
        <ul style={{ marginLeft: '20px' }}>
          <li><strong>Hedge Funds:</strong> Institutional investment funds</li>
          <li><strong>Foundations:</strong> Crypto foundations and treasuries</li>
          <li><strong>Whales:</strong> High-net-worth individuals</li>
          <li><strong>HFT/Algo Firms:</strong> Algorithmic and high-frequency traders</li>
        </ul>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>My Liquidity Provider Type</h4>
        <p>
          Your classification determines which inbound requests you receive. Other participants
          will only broadcast to you if they've authorized your type.
        </p>
        <ul style={{ marginLeft: '20px' }}>
          <li><strong>Not Offering Liquidity:</strong> You won't receive inbound requests</li>
          <li><strong>Other options:</strong> You'll receive requests from participants who authorize your type</li>
        </ul>
      </div>

    </div>
  );
}

function SecurityContent() {
  return (
    <div>
      <h3 style={{ color: '#26a69a', marginTop: 0 }}>Security & Privacy</h3>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>Hybrid Encryption</h4>
        <p>
          Your order data is protected using state-of-the-art hybrid encryption:
        </p>
        <ul style={{ marginLeft: '20px' }}>
          <li><strong>ML-KEM-768:</strong> Post-quantum key encapsulation (NIST FIPS 203)</li>
          <li><strong>ECDH P-256:</strong> Elliptic curve key exchange</li>
          <li><strong>AES-256-GCM:</strong> Authenticated encryption for the payload</li>
        </ul>
        <p style={{ marginTop: '12px' }}>
          This combination ensures your data is secure against both current and future quantum computing threats.
        </p>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>Secure Enclave</h4>
        <p>
          All matching occurs inside an AWS Nitro Enclave - an isolated compute environment with
          cryptographic attestation. Key properties:
        </p>
        <ul style={{ marginLeft: '20px' }}>
          <li>No persistent storage - data exists only in memory during matching</li>
          <li>No external network access from the enclave</li>
          <li>Cryptographic attestation proves the enclave is running unmodified code</li>
          <li>Even cloud administrators cannot access enclave memory</li>
        </ul>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#fff', marginBottom: '12px' }}>What We Can't See</h4>
        <p>
          The platform operators have <strong>zero knowledge</strong> of:
        </p>
        <ul style={{ marginLeft: '20px' }}>
          <li>Your order side (buy or sell) before matching</li>
          <li>Your order quantity before matching</li>
          <li>Your limit price before matching</li>
          <li>Unmatched orders (they are discarded without logging)</li>
        </ul>
      </div>

      <div style={{
        backgroundColor: 'rgba(38, 166, 154, 0.1)',
        border: '1px solid rgba(38, 166, 154, 0.3)',
        borderRadius: '8px',
        padding: '16px'
      }}>
        <strong style={{ color: '#26a69a' }}>Verification:</strong> The enclave's cryptographic attestation
        can be verified to ensure you're communicating with genuine, unmodified matching software.
      </div>
    </div>
  );
}

function LiquidityContent() {
  const thStyle = { padding: '8px 10px', color: '#26a69a', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #333', textAlign: 'left' };
  const tdStyle = { padding: '8px 10px', fontSize: '13px', color: '#ccc', borderBottom: '1px solid #222', verticalAlign: 'top', whiteSpace: 'nowrap' };
  const tdFirst = { ...tdStyle, color: '#fff', fontWeight: '500', whiteSpace: 'nowrap' };

  return (
    <div>
      <h3 style={{ color: '#26a69a', marginTop: 0 }}>Liquidity Participants</h3>
      <p style={{ marginBottom: '16px', fontSize: '13px' }}>
        The platform includes simulated participants across four categories. Response rates reflect the
        probability of quoting at all — each quote is randomly BUY or SELL, so roughly half will be on
        the wrong side and won't match your order.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Participants</th>
              <th style={thStyle}>Response rate</th>
              <th style={thStyle}>Speed</th>
              <th style={thStyle}>Notional size</th>
              <th style={thStyle}>Price tolerance</th>
              <th style={thStyle}>Initiates every</th>
              <th style={thStyle}>Min window</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdFirst}>HFT / Algo</td>
              <td style={tdStyle}>12</td>
              <td style={tdStyle}>~95%</td>
              <td style={tdStyle}>0.1 – 1 s</td>
              <td style={tdStyle}>$250K – $2.5M</td>
              <td style={tdStyle}>0.02 – 0.1%</td>
              <td style={tdStyle}>~5 min</td>
              <td style={tdStyle}>5 s</td>
            </tr>
            <tr>
              <td style={tdFirst}>Hedge Funds</td>
              <td style={tdStyle}>9</td>
              <td style={tdStyle}>~40%</td>
              <td style={tdStyle}>3 – 15 s</td>
              <td style={tdStyle}>$1M – $15M</td>
              <td style={tdStyle}>0.1 – 0.5%</td>
              <td style={tdStyle}>~10 min</td>
              <td style={tdStyle}>15 s</td>
            </tr>
            <tr>
              <td style={tdFirst}>Foundations</td>
              <td style={tdStyle}>15</td>
              <td style={tdStyle}>~50%</td>
              <td style={tdStyle}>5 – 20 s</td>
              <td style={tdStyle}>$1.5M – $30M</td>
              <td style={tdStyle}>0.2 – 0.8%</td>
              <td style={tdStyle}>~15 min</td>
              <td style={tdStyle}>20 s</td>
            </tr>
            <tr>
              <td style={tdFirst}>Whales</td>
              <td style={tdStyle}>6</td>
              <td style={tdStyle}>~30%</td>
              <td style={tdStyle}>10 – 60 s</td>
              <td style={tdStyle}>$7.5M – $150M</td>
              <td style={tdStyle}>0.3 – 1.0%</td>
              <td style={tdStyle}>15 – 20 min</td>
              <td style={tdStyle}>60 s</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '12px' }}>
        <div style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '6px', padding: '10px 12px' }}>
          <div style={{ color: '#26a69a', fontWeight: '600', marginBottom: '4px' }}>Off-hours (22:00 – 06:00 UTC)</div>
          <div style={{ color: '#aaa', lineHeight: '1.6' }}>
            HFT ~65% · Hedge Funds ~22% · Foundations ~21% · Whales ~11%
          </div>
        </div>
        <div style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '6px', padding: '10px 12px' }}>
          <div style={{ color: '#26a69a', fontWeight: '600', marginBottom: '4px' }}>Weekends</div>
          <div style={{ color: '#aaa', lineHeight: '1.6' }}>
            All categories drop to ~60% of their weekday rate.
          </div>
        </div>
      </div>

      <p style={{ fontSize: '12px', color: '#666', marginTop: '12px' }}>
        All times UTC. Active hours: 06:00 – 22:00 UTC weekdays.
      </p>
    </div>
  );
}

export default HelpModal;
